import {
	Bug,
	type LucideIcon,
	PackageOpen,
	Sparkles,
	Store,
	Trash2,
	TrendingDown,
	TrendingUp,
	TriangleAlert,
} from "lucide-react";

export type EventMeta = {
	Icon: LucideIcon;
	label: string;
	/** The badge as it sits on a paper feed card. */
	chip: string;
	/** The same event as a selected filter chip, which sits on the ground. */
	chipActive: string;
	iconColor: string;
};

/**
 * Event badges are tinted by what the event means for buying the thing, which
 * is the only question the stream exists to answer:
 *
 *   live   the item is obtainable now — it appeared, came back, or landed in a shop
 *   drop   it got cheaper
 *   needs  it got dearer
 *   fault  a store adapter broke
 *   inert  neither: it is gone, or it is a debug row
 *
 * This is a deliberate widening of the house rule that colour signals product
 * state and event type is carried by icon shape alone. Eight badges in one
 * flat ink-on-paper treatment left the size-3 icon as the only difference
 * between them, which is not a difference you can see while scrolling. The
 * rule it keeps is the one that matters: colour is never the only signal —
 * every badge still carries its word, and the icon still separates the three
 * events that share the `live` hue.
 */
type Hue = "live" | "drop" | "needs" | "fault" | "inert";

// A 10% wash of the hue under the hue itself. The tokens in theme.css clear
// 4.5 against the ground with room to spare (the weakest, light-mode drop,
// is 5.03), and a 10% wash over paper costs well under half a point — so the
// badge keeps the floor without the old outline, which doubled every badge's
// visual weight on a dense list.
const CHIP: Record<Hue, string> = {
	live: "bg-live/10 text-live",
	drop: "bg-drop/10 text-drop",
	needs: "bg-needs/10 text-needs",
	fault: "bg-fault/10 text-fault",
	inert: "bg-raised text-muted",
};

// A selected filter chip: the wash deepens and the hue takes the border, so
// selection reads without relying on the tint alone.
const CHIP_ACTIVE: Record<Hue, string> = {
	live: "border border-live/60 bg-live/15 text-live",
	drop: "border border-drop/60 bg-drop/15 text-drop",
	needs: "border border-needs/60 bg-needs/15 text-needs",
	fault: "border border-fault/60 bg-fault/15 text-fault",
	inert: "border border-muted bg-raised text-ink",
};

const ICON_COLOR: Record<Hue, string> = {
	live: "text-live",
	drop: "text-drop",
	needs: "text-needs",
	fault: "text-fault",
	inert: "text-muted",
};

function meta(Icon: LucideIcon, label: string, hue: Hue): EventMeta {
	return {
		Icon,
		label,
		chip: CHIP[hue],
		chipActive: CHIP_ACTIVE[hue],
		iconColor: ICON_COLOR[hue],
	};
}

const EVENT_META: Record<string, EventMeta> = {
	new_product: meta(Sparkles, "New", "live"),
	restock: meta(PackageOpen, "Restock", "live"),
	price_drop: meta(TrendingDown, "Price drop", "drop"),
	price_increase: meta(TrendingUp, "Price up", "needs"),
	store_restock: meta(Store, "In store", "live"),
	removed: meta(Trash2, "Removed", "inert"),
	adapter_error: meta(TriangleAlert, "Error", "fault"),
	debug: meta(Bug, "Debug", "inert"),
};

export const EVENT_ORDER: ReadonlyArray<string> = [
	"new_product",
	"restock",
	"price_drop",
	"price_increase",
	"store_restock",
	"removed",
	"adapter_error",
	"debug",
];

export function metaFor(type: string): EventMeta {
	return EVENT_META[type] ?? meta(Sparkles, type, "inert");
}
